import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ProjectElementType } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { CommentsCount } from "../../commentsCount";
import { EventIcon } from "@common/eventIcon";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { ActivityWatcher } from "@common/activityWatcher";
import { TransferIcon } from "@common/transferIcon";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Props } from "./types";
import { NameEditionForm } from "./nameEditionForm";
import styles from "./styles.scss";

function Event({ event, project, list }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div className={styles.event}>
      <div
        className={styles.eventDetails}
        onClick={() =>
          pushView({
            key: UseCases.retrieveEvent,
            data: { event },
          })
        }
      >
        <div className={styles.name}>
          <div
            className={styles.eventCompletionStatus}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <EventIcon className={styles.icon} />
          </div>
          <NameEditionForm event={event} />
        </div>
        <p className={styles.status} />
        <div className={styles.allocations} />
        {event.endTime ? (
          <p className={styles.text}>
            {new Date(event.endTime).toLocaleDateString()}
          </p>
        ) : (
          <p className={styles.text} />
        )}
        <CommentsCount
          subject={event.uuid}
          type="event"
          commentsNbr={event.commentsNbr}
        />
      </div>
      <div
        className={classNames(styles.contextualMenu, {
          [styles.displayContextualMenu]: showContextualMenu,
        })}
      >
        <div ref={ref}>
          <SecureContextualMenu
            context={{ event }}
            replacement={<div className={styles.placeholder} />}
            items={[
              {
                name: t("listsList.contextualMenu.edit"),
                useCase: UseCases.editEvent,
                renderIcon: (className) => <EditIcon className={className} />,
                act: () =>
                  pushView({
                    data: { event },
                    key: UseCases.retrieveEvent,
                  }),
              },
              {
                name: t("listsList.contextualMenu.move"),
                useCase: UseCases.moveListElement,
                renderIcon: (className) => (
                  <TransferIcon className={className} />
                ),
                act: () =>
                  pushView({
                    key: UseCases.moveListElement,
                    data: {
                      projectElementId: event.uuid,
                      projectElementType: ProjectElementType.EVENT,
                      currentParentList: list,
                      project,
                    },
                  }),
              },
              {
                name: t("listsList.contextualMenu.remove"),
                useCase: UseCases.removeEvent,
                renderIcon: (className) => (
                  <TrashIcon
                    className={classNames(className, styles.removeIcon)}
                  />
                ),
                color: "#db5f63",
                act: () =>
                  pushView({
                    data: event,
                    key: UseCases.removeEvent,
                  }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export { Event };
